import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/axiosConfig'; 
import './EmployerDashboard.css';
import { 
  FaUser, FaHistory, FaFileAlt, FaMoneyBillWave, 
  FaCheckCircle, FaClock, FaTimesCircle, FaPlus,FaChevronUp,
  FaChartLine, FaUsers, FaCalendarAlt, FaDownload,
  FaTachometerAlt, FaSignOutAlt, FaBars, FaTimes,
  FaBell, FaBuilding, FaGraduationCap, FaEnvelope,
  FaCog, FaSearch, FaEye, FaEdit, FaTrash,
  FaSpinner, FaExclamationTriangle, FaFilter, FaBolt,
  FaChartPie, FaFileInvoice, FaUserCheck, FaCalendar,
  FaPhone, FaMapMarkerAlt, FaIndustry, FaSync,
  FaChevronRight, FaInfoCircle, FaStar, FaChevronLeft,
  FaHome, FaBriefcase, FaEllipsisH,
  FaLanguage, FaAward, FaSchool, FaTools, FaInfo
} from 'react-icons/fa';

// Components
import RequestModal from '../components/RequestModal';
import ConfirmationModal from '../components/ConfirmationModal';
import EditRequestModal from '../components/EditRequestModal';

// Translation Context
import { useTranslation } from '../contexts/TranslationContext';



// Helper functions
const safeToFixed = (value, decimals = 1) => {
  const num = parseFloat(value);
  return isNaN(num) ? '0.0' : num.toFixed(decimals);
};

const safeParseFloat = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

const safeParseInt = (value) => {
  const num = parseInt(value);
  return isNaN(num) ? 0 : num;
};

const formatCurrency = (value) => {
  const num = safeParseFloat(value);
  return num.toLocaleString() + ' RWF';
};

// Helper to format specification values
const formatSpecification = (value, type = 'text') => {
  if (!value) return '-';
  
  switch(type) {
    case 'age':
      return value;
    case 'experience':
      return value.charAt(0).toUpperCase() + value.slice(1);
    case 'skill':
    case 'education':
      return value.charAt(0).toUpperCase() + value.slice(1);
    case 'language':
      return value.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join('/');
    default:
      return value;
  }
};

const EmployerDashboard = ({ user, onLogout }) => {
  const { t, language } = useTranslation();
  
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({
    total_requests: 0,
    approved_requests: 0,
    pending_requests: 0,
    
    total_hired: 0,
    approval_rate: 0,
    avg_employees_per_request: 0,
    avg_duration_days: 0,
    hired_women: []
  });
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hiredWomen, setHiredWomen] = useState([]);
  const [contractPeriods, setContractPeriods] = useState([]);
const [competenceSkills, setCompetenceSkills] = useState({}); // keyed by department_id
  const [filters, setFilters] = useState({
    status: 'all',
    department: 'all',
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  const [notifications, setNotifications] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState({});
  const [expandedRow, setExpandedRow] = useState(null);


  // Profile settings state
const [profileData, setProfileData] = useState({
  full_name: user?.full_name || '',
  email: user?.email || '',
  phone: user?.phone || '',
  company_name: user?.company_name || ''
});
const [savingProfile, setSavingProfile] = useState(false);

  // Add these state variables in EmployerDashboard.js
const [activeContracts, setActiveContracts] = useState([]);
const [contractHistory, setContractHistory] = useState([]);
const [contractStats, setContractStats] = useState({ active: 0, expiringSoon: 0, completed: 0 });
const [contractTab, setContractTab] = useState('active');
const [showRenewModal, setShowRenewModal] = useState(false);
const [showTerminateModal, setShowTerminateModal] = useState(false);
const [showContractDetailsModal, setShowContractDetailsModal] = useState(false);
const [selectedContract, setSelectedContract] = useState(null);
const [renewData, setRenewData] = useState({
  contract_period_id: '',
  months: 3,
  send_email_notification: true
});
const [terminateData, setTerminateData] = useState({
  reason: '',
  custom_reason: '',
  effective_date: '',
  send_email_notification: true
});
const [renewLoading, setRenewLoading] = useState(false);
const [terminateLoading, setTerminateLoading] = useState(false);

  // Set up axios interceptor for authentication
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  // Add a new fetch function
  const fetchHiredWomen = useCallback(async () => {
    try {
      const response = await axios.get('https://hsn.urugowoc.org.rw/backend/api/employer/hired-women', { params: { lang: undefined  } });
      setHiredWomen(response.data || []);
    } catch (error) {
      console.error('Error fetching hired women:', error);
      setError(t('failedLoadHiredWomen') || 'Failed to load hired women data');
    }
  }, [language, t]);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [requestsRes, statsRes, deptsRes, hiredRes] = await Promise.all([
        axios.get('https://hsn.urugowoc.org.rw/backend/api/employer/my-requests', { 
          params: { 
            lang: undefined ,
            include_specifications: true,  // Add this
            include_competence_skills: true,  // Add this
            include_contract_period: true  // Add this
          } 
        }),
        axios.get('https://hsn.urugowoc.org.rw/backend/api/employer/stats', { params: { lang: undefined  } }),
        axios.get('https://hsn.urugowoc.org.rw/backend/api/departments', { params: { lang: undefined  } }),
        axios.get('https://hsn.urugowoc.org.rw/backend/api/employer/hired-women', { params: { lang: undefined  } })
      ]);
      
      // Filter requests to ensure they belong to current user
      const myRequests = verifyEmployerData(requestsRes.data || []);
      const myHiredWomen = verifyEmployerData(hiredRes.data || []);
      
      console.log('Fetched requests with specs:', myRequests); // Debug log
      
      setRequests(myRequests);
      setHiredWomen(myHiredWomen);


      // Also refresh contracts
try {
  const [activeRes, historyRes] = await Promise.all([
    axios.get('https://hsn.urugowoc.org.rw/backend/api/employer/my-contracts'),
    axios.get('https://hsn.urugowoc.org.rw/backend/api/employer/contract-history')
  ]);
  setActiveContracts(activeRes.data || []);
  setContractHistory(historyRes.data || []);
  setContractStats({
    active: (activeRes.data || []).filter(c => c.status === 'active').length,
    expiringSoon: (activeRes.data || []).filter(c => c.days_remaining <= 7 && c.days_remaining > 0).length,
    completed: (historyRes.data || []).length
  });
} catch (contractErr) {
  console.warn('Contract fetch failed (non-critical):', contractErr.message);
}
        
      const safeStats = {
        total_requests: safeParseInt(statsRes.data?.total_requests),
        approved_requests: safeParseInt(statsRes.data?.approved_requests),
        pending_requests: safeParseInt(statsRes.data?.pending_requests),
        
        total_hired: safeParseInt(statsRes.data?.total_hired),
        approval_rate: safeParseFloat(statsRes.data?.approval_rate),
        avg_employees_per_request: safeParseFloat(statsRes.data?.avg_employees_per_request),
        avg_duration_days: safeParseFloat(statsRes.data?.avg_duration_days),
        hired_women: Array.isArray(statsRes.data?.hired_women) ? statsRes.data.hired_women : []
      };
      
      setStats(safeStats);
      setDepartments(deptsRes.data || []);
      
      // Generate notifications
      const newNotifications = [];
      
      const pendingCount = (requestsRes.data || []).filter(req => req.status === 'pending').length;
      if (pendingCount > 0) {
        newNotifications.push({
          id: 1,
          type: 'warning',
          message: t('notificationPendingRequests', { count: pendingCount }) || `You have ${pendingCount} pending request${pendingCount > 1 ? 's' : ''}`,
          time: t('justNow') || 'Just now',
          action: () => setActiveMenu('requests')
        });
      }
      
     
      
      setNotifications(newNotifications);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(error.response?.data?.error || t('failedLoadDashboard') || 'Failed to load dashboard data');
      
      if (error.response?.status === 401) {
        onLogout();
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [onLogout, language, t]);



  useEffect(() => {
  setProfileData({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    company_name: user?.company_name || ''
  });
}, [user]);



const handleSaveProfile = async () => {
  setSavingProfile(true);
  setError(null);
  setSuccessMessage(null);

  if (!profileData.full_name || profileData.full_name.trim().length < 2) {
    setError(t('enterFullName') || 'Please enter a valid full name');
    setSavingProfile(false);
    return;
  }

  if (!profileData.email || !profileData.email.includes('@')) {
    setError(t('enterValidEmail') || 'Please enter a valid email');
    setSavingProfile(false);
    return;
  }

  try {
    const response = await axios.put(
      'https://hsn.urugowoc.org.rw/backend/api/employer/profile',
      {
        full_name: profileData.full_name.trim(),
        email: profileData.email.trim().toLowerCase(),
        phone: profileData.phone ? profileData.phone.trim() : null,
        company_name: profileData.company_name ? profileData.company_name.trim() : null
      }
    );

    // Update localStorage user
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const updatedUser = { ...storedUser, ...response.data.user };
    localStorage.setItem('user', JSON.stringify(updatedUser));

    setSuccessMessage(t('profileUpdated') || 'Profile updated successfully!');
    setTimeout(() => setSuccessMessage(null), 5000);
  } catch (error) {
    console.error('Error updating profile:', error);
    setError(error.response?.data?.error || t('failedUpdateProfile') || 'Failed to update profile');
  } finally {
    setSavingProfile(false);
  }
};
 
  // Add this helper function to your EmployerDashboard.js
  const verifyEmployerData = (data) => {
    // Ensure all requests belong to logged-in employer
    const userEmail = user?.email;
    const filteredData = data.filter(item => 
      item.employer_email === userEmail || 
      item.assigned_employer_email === userEmail ||
      !item.employer_email // Keep items without email for backward compatibility
    );
    
    console.log(`Filtered ${data.length - filteredData.length} items not belonging to ${userEmail}`);
    return filteredData;
  };

  useEffect(() => {
    fetchDashboardData();
    
    const interval = setInterval(fetchDashboardData, 600000);
    
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  

  const handleRequestSubmit = async (requestData) => {
    try {
      const response = await axios.post('https://hsn.urugowoc.org.rw/backend/api/employer/request', requestData);
      setSuccessMessage(t('requestSubmittedSuccess') || 'Request submitted successfully!');
      setShowRequestModal(false);
      fetchDashboardData();
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Error submitting request:', error);
      setError(t('failedSubmitRequest') || 'Failed to submit request. Please try again.');
    }
  };

  // Add these fetch functions in EmployerDashboard.js
const fetchContracts = useCallback(async () => {
  setRefreshing(true);
  try {
    const [activeRes, historyRes] = await Promise.all([
      axios.get('https://hsn.urugowoc.org.rw/backend/api/employer/my-contracts', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      }),
      axios.get('https://hsn.urugowoc.org.rw/backend/api/employer/contract-history', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
    ]);
    
    setActiveContracts(activeRes.data || []);
    setContractHistory(historyRes.data || []);
    
    // Calculate stats
    const active = activeRes.data.filter(c => c.status === 'active').length;
    const expiringSoon = activeRes.data.filter(c => c.days_remaining <= 7).length;
    const completed = historyRes.data.length;
    
    setContractStats({ active, expiringSoon, completed });
    
  } catch (error) {
    console.error('Error fetching contracts:', error);
    setError(t('failedLoadContracts') || 'Failed to load contracts');
  } finally {
    setRefreshing(false);
  }
}, [t]);

const fetchContractDetails = async (contractId) => {
  try {
    const response = await axios.get(`https://hsn.urugowoc.org.rw/backend/api/employer/contract/${contractId}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    setSelectedContract(response.data);
    setShowContractDetailsModal(true);
  } catch (error) {
    console.error('Error fetching contract details:', error);
    setError(t('failedLoadContractDetails') || 'Failed to load contract details');
  }
};


// Fetch contracts when employer logs in
useEffect(() => {
  fetchContracts();
}, [fetchContracts]);


const handleRenewContract = async () => {
  if (!selectedContract) return;
  
  let contractPeriodId = renewData.contract_period_id;
  let months = renewData.months;
  
  if (contractPeriodId === 'custom') {
    if (!months || months < 1) {
      setError(t('enterValidMonths') || 'Please enter a valid number of months');
      return;
    }
    contractPeriodId = null; // Use custom months
  }
  
  setRenewLoading(true);
  setError(null);
  
  try {
    const response = await axios.post(
      `https://hsn.urugowoc.org.rw/backend/api/employer/contract/${selectedContract.id}/renew`,
      {
        contract_period_id: contractPeriodId,
        months: contractPeriodId === null ? months : undefined,
        send_email: renewData.send_email_notification
      },
      {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      }
    );
    
    setSuccessMessage(t('contractRenewedSuccess') || 'Contract renewed successfully!');
    setShowRenewModal(false);
    setSelectedContract(null);
    setRenewData({
      contract_period_id: '',
      months: 3,
      send_email_notification: true
    });
    
    // Refresh contracts
    fetchContracts();
    
  } catch (error) {
    console.error('Error renewing contract:', error);
    setError(error.response?.data?.error || t('failedRenewContract') || 'Failed to renew contract');
  } finally {
    setRenewLoading(false);
  }
};

const handleTerminateContract = async () => {
  if (!selectedContract) return;
  
  let reason = terminateData.reason;
  if (reason === 'other') {
    reason = terminateData.custom_reason;
  }
  
  if (!reason) {
    setError(t('enterTerminationReason') || 'Please provide a termination reason');
    return;
  }
  
  setTerminateLoading(true);
  setError(null);
  
  try {
    const response = await axios.post(
      `https://hsn.urugowoc.org.rw/backend/api/employer/contract/${selectedContract.id}/terminate`,
      {
        reason: reason,
        effective_date: terminateData.effective_date || undefined,
        send_email: terminateData.send_email_notification
      },
      {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      }
    );
    
    setSuccessMessage(t('contractTerminatedSuccess') || 'Contract terminated successfully!');
    setShowTerminateModal(false);
    setSelectedContract(null);
    setTerminateData({
      reason: '',
      custom_reason: '',
      effective_date: '',
      send_email_notification: true
    });
    
    // Refresh contracts
    fetchContracts();
    
  } catch (error) {
    console.error('Error terminating contract:', error);
    setError(error.response?.data?.error || t('failedTerminateContract') || 'Failed to terminate contract');
  } finally {
    setTerminateLoading(false);
  }
};

const viewContractDetails = (contractId) => {
  fetchContractDetails(contractId);
};

const openRenewContractModal = (contract) => {
  setSelectedContract(contract);
  setRenewData({
    contract_period_id: contract.contract_period_id || '',
    months: contract.months || 3,
    send_email_notification: true
  });
  setShowRenewModal(true);
};

const openTerminateContractModal = (contract) => {
  setSelectedContract(contract);
  setTerminateData({
    reason: '',
    custom_reason: '',
    effective_date: '',
    send_email_notification: true
  });
  setShowTerminateModal(true);
};
// Add a function to fetch contract periods
const fetchContractPeriods = useCallback(async () => {
  try {
    const response = await axios.get('https://hsn.urugowoc.org.rw/backend/api/contract-periods', {
      params: { lang: undefined  }
    });
    setContractPeriods(response.data || []);
  } catch (error) {
    console.error('Error fetching contract periods:', error);
  }
}, [language]);

// Add a function to fetch competence skills by department
const fetchCompetenceSkills = useCallback(async (departmentId) => {
  if (!departmentId) return;
  
  try {
    const response = await axios.get(`https://hsn.urugowoc.org.rw/backend/api/competence-skills/${departmentId}`, {
      params: { lang: undefined  }
    });
    setCompetenceSkills(prev => ({
      ...prev,
      [departmentId]: response.data || []
    }));
  } catch (error) {
    console.error('Error fetching competence skills:', error);
  }
}, [language]);

// Update useEffect to fetch contract periods
useEffect(() => {
  fetchDashboardData();
  fetchContractPeriods(); // Add this
  
  const interval = setInterval(fetchDashboardData, 600000);
  
  return () => clearInterval(interval);
}, [fetchDashboardData]);

// Add this helper function for formatting contract period
const formatContractPeriod = (request) => {
  if (request.contract_period_name) {
    return request.contract_period_name;
  } else if (request.duration_days) {
    return `${request.duration_days} days`;
  }
  return '-';
};
  const handleEditRequest = (request) => {
    setSelectedRequest(request);
    setShowEditModal(true);
  };

  const handleUpdateRequest = async (requestId, updates) => {
    try {
      const response = await axios.put(`https://hsn.urugowoc.org.rw/backend/api/employer/request/${requestId}`, updates);
      setSuccessMessage(t('requestUpdatedSuccess') || 'Request updated successfully!');
      setShowEditModal(false);
      setSelectedRequest(null);
      fetchDashboardData();
      
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Error updating request:', error);
      setError(t('failedUpdateRequest') || 'Failed to update request. Please try again.');
    }
  };

  const handleDeleteRequest = (request) => {
    setSelectedRequest(request);
    setShowDeleteModal(true);
  };

  const confirmDeleteRequest = async () => {
    if (!selectedRequest) return;
    
    try {
      await axios.delete(`https://hsn.urugowoc.org.rw/backend/api/employer/request/${selectedRequest.id}`);
      setSuccessMessage(t('requestDeletedSuccess') || 'Request deleted successfully!');
      setShowDeleteModal(false);
      setSelectedRequest(null);
      fetchDashboardData();
      
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Error deleting request:', error);
      setError(t('failedDeleteRequest') || 'Failed to delete request. Please try again.');
    }
  };

  const handleCancelRequest = async (request) => {
    if (!window.confirm(t('confirmCancelRequest') || 'Are you sure you want to cancel this request?')) return;
    
    try {
      await axios.put(`https://hsn.urugowoc.org.rw/backend/api/employer/request/${request.id}/status`, { status: 'cancelled' });
      setSuccessMessage(t('requestCancelledSuccess') || 'Request cancelled successfully!');
      fetchDashboardData();
      
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Error cancelling request:', error);
      setError(t('failedCancelRequest') || 'Failed to cancel request. Please try again.');
    }
  };

  
  const exportRequests = async () => {
    setExporting(true);
    try {
      const response = await axios.get('https://hsn.urugowoc.org.rw/backend/api/employer/export-requests', {
        responseType: 'blob',
        params: { lang: undefined  }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `my_requests_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setSuccessMessage(t('exportSuccessful') || 'Export successful! File downloaded.');
      setTimeout(() => setSuccessMessage(null), 5000);
      
    } catch (error) {
      console.error('Error exporting requests:', error);
      setError(t('failedExportRequests') || 'Failed to export requests');
    } finally {
      setExporting(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      status: 'all',
      department: 'all',
      start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
      end: new Date().toISOString().split('T')[0]
    });
    setSearchTerm('');
  };

  const toggleRowExpansion = (requestId) => {
    setExpandedRow(expandedRow === requestId ? null : requestId);
  };

  const filteredRequests = (requests || []).filter(request => {
    if (!request) return false;
    
    const matchesSearch = searchTerm === '' || 
      (request.department_name && request.department_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (request.status && request.status.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (request.id && request.id.toString().includes(searchTerm)) ||
      (request.age_range && request.age_range.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (request.experience_level && request.experience_level.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (request.skill_level && request.skill_level.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = filters.status === 'all' || request.status === filters.status;
    const matchesDepartment = filters.department === 'all' || 
      (request.department_id && request.department_id.toString() === filters.department);
    
    try {
      const requestDate = new Date(request.created_at);
      const startDate = new Date(filters.start);
      const endDate = new Date(filters.end);
      endDate.setHours(23, 59, 59, 999);
      const matchesDate = requestDate >= startDate && requestDate <= endDate;
      
      return matchesSearch && matchesStatus && matchesDepartment && matchesDate;
    } catch (e) {
      return matchesSearch && matchesStatus && matchesDepartment;
    }
  });

  

 
  const menuItems = [
    { id: 'dashboard', label: t('dashboard'), icon: <FaTachometerAlt />, mobileIcon: <FaHome /> },
  ];

  // Mobile menu items (simplified for mobile)
  const mobileMenuItems = [
    { id: 'dashboard', label: t('home'), icon: <FaHome /> },
    { id: 'requests', label: t('myRequests'), icon: <FaBriefcase /> },
    { id: 'hired', label: t('hiredWomen'), icon: <FaUsers /> },
    
    { id: 'contracts', label: t('contracts') || 'Contracts', icon: <FaFileAlt /> },
    { id: 'settings', label: t('settings'), icon: <FaCog />, mobileIcon: <FaCog /> }
  ];

  if (loading) {
    return (
      <div className="employer-dashboard-loading">
        <div className="employer-loading-spinner"></div>
        <p>{t('loadingDashboard') || 'Loading your dashboard...'}</p>
      </div>
    );
  }

  return (
    <div className="employer-dashboard">
      {/* Mobile Header */}
      <header className="employer-mobile-header">
        <button 
          className="employer-mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={t('toggleMenu') || 'Toggle menu'}
        >
          {mobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>
        <h1 className="employer-mobile-title">
          {menuItems.find(item => item.id === activeMenu)?.label}
        </h1>
        <div className="employer-mobile-actions">
          <button 
            className="employer-btn-notification-mobile"
            onClick={() => setActiveMenu('requests')}
            aria-label={t('notifications') || 'Notifications'}
          >
            <FaBell />
            {notifications.length > 0 && (
              <span className="employer-notification-count-mobile">{notifications.length}</span>
            )}
          </button>
          <button 
            className="employer-user-avatar-mobile"
            onClick={() => setActiveMenu('settings')}
          >
            {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'E'}
          </button>
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      <nav className="employer-mobile-nav">
        {mobileMenuItems.map(item => (
          <button
            key={item.id}
            className={`employer-mobile-nav-item ${activeMenu === item.id ? 'active' : ''}`}
            onClick={() => setActiveMenu(item.id)}
          >
            <span className="employer-mobile-nav-icon">{item.icon}</span>
            <span className="employer-mobile-nav-label">{item.label}</span>
          </button>
        ))}
        <button
          className="employer-mobile-nav-item"
          onClick={() => setShowRequestModal(true)}
        >
          <span className="employer-mobile-nav-icon employer-new-request-icon">
            <FaPlus />
          </span>
          <span className="employer-mobile-nav-label">{t('newRequest')}</span>
        </button>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="employer-mobile-menu-overlay">
          <div className="employer-mobile-menu-content">
            <div className="employer-mobile-menu-header">
              <div className="employer-user-info-mobile">
                <div className="employer-user-avatar-mobile">
                  {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'E'}
                </div>
                <div>
                  <h4>{user?.full_name || user?.username}</h4>
                  <p>{user?.email}</p>
                  <span className="employer-user-role">{t('employer')}</span>
                </div>
              </div>
              <button 
                className="employer-mobile-menu-close"
                onClick={() => setMobileMenuOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="employer-mobile-menu-items">
              {menuItems.map(item => (
                <button
                  key={item.id}
                  className={`employer-mobile-menu-item ${activeMenu === item.id ? 'active' : ''}`}
                  onClick={() => {
                    setActiveMenu(item.id);
                    setMobileMenuOpen(false);
                  }}
                >
                  <span className="employer-mobile-menu-icon">{item.icon}</span>
                  <span>{item.label}</span>
                  <FaChevronRight className="employer-mobile-menu-arrow" />
                </button>
              ))}
              
              <div className="employer-mobile-menu-divider"></div>
              
              <button className="employer-mobile-menu-item" onClick={handleRefresh}>
                <FaSync /> {t('refresh') || 'Refresh Data'}
              </button>
              <button className="employer-mobile-menu-item" onClick={onLogout}>
                <FaSignOutAlt /> {t('logout')}
              </button>
            </div>
          </div>
          <div 
            className="employer-mobile-menu-backdrop"
            onClick={() => setMobileMenuOpen(false)}
          />
        </div>
      )}

      {/* Main Content */}
      <main className="employer-dashboard-main">
        {/* Desktop Header */}
        <header className="employer-dashboard-header">
          <div className="employer-header-left">
            <div className="employer-logo">
              <FaBuilding />
              <span>{t('urugoWOC') || 'Urugo WOC'}</span>
            </div>
            <nav className="employer-desktop-nav">
              {menuItems.map(item => (
                <button
                  key={item.id}
                  className={`employer-desktop-nav-item ${activeMenu === item.id ? 'active' : ''}`}
                  onClick={() => setActiveMenu(item.id)}
                >
                  <span className="employer-desktop-nav-icon">{item.icon}</span>
                  <span className="employer-desktop-nav-label">{item.label}</span>
                </button>
              ))}
              <button 
                className="employer-desktop-new-request"
                onClick={() => setShowRequestModal(true)}
              >
                <FaPlus /> {t('newRequest')}
              </button>
            </nav>
          </div>
          <div className="employer-header-right">
            <div className="employer-notification-dropdown">
              <button 
                className="employer-btn-notification"
                aria-label={`${notifications.length} ${t('notifications') || 'notifications'}`}
              >
                <FaBell />
                {notifications.length > 0 && (
                  <span className="employer-notification-count">{notifications.length}</span>
                )}
              </button>
              <div className="employer-notification-dropdown-content">
                <h4>{t('notifications')}</h4>
                {notifications.length > 0 ? (
                  notifications.map(notification => (
                    <div 
                      key={notification.id} 
                      className={`employer-notification-item ${notification.type}`}
                      onClick={notification.action}
                    >
                      <div className="employer-notification-icon">
                        {notification.type === 'warning' && <FaClock />}
                        
                        {notification.type === 'success' && <FaCheckCircle />}
                      </div>
                      <div className="employer-notification-content">
                        <p>{notification.message}</p>
                        <span>{notification.time}</span>
                      </div>
                      <FaChevronRight />
                    </div>
                  ))
                ) : (
                  <p className="employer-no-notifications">{t('noNotifications') || 'No notifications'}</p>
                )}
              </div>
            </div>
            
            <div className="employer-user-profile">
              <div className="employer-user-avatar">
                {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'E'}
              </div>
              <div className="employer-user-details">
                <h4>{user?.full_name || user?.username}</h4>
                <p>{user?.email}</p>
                <span className="employer-user-role">{t('employer')}</span>
              </div>
              <div className="employer-user-dropdown-menu">
                <button className="employer-btn-refresh-header" onClick={handleRefresh} disabled={refreshing}>
                  {refreshing ? <FaSpinner className="employer-spinning" /> : <FaSync />}
                </button>
                <button className="employer-logout-btn" onClick={onLogout}>
                  <FaSignOutAlt />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Messages */}
        {error && (
          <div className="employer-message-banner employer-error">
            <FaExclamationTriangle />
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}
        
        {successMessage && (
          <div className="employer-message-banner employer-success">
            <FaCheckCircle />
            <span>{successMessage}</span>
            <button onClick={() => setSuccessMessage(null)}>×</button>
          </div>
        )}

        {/* Content Area */}
        <div className="employer-dashboard-content">
          {/* Dashboard Overview */}
          {activeMenu === 'dashboard' && (
            <>
              {/* Quick Stats Bar */}
              <div className="employer-quick-stats-bar">
                <div className="employer-quick-stat-item">
                  <div className="employer-quick-stat-icon">
                    <FaFileAlt />
                  </div>
                  <div>
                    <h4>{stats.total_requests}</h4>
                    <p>{t('totalRequests')}</p>
                  </div>
                </div>
                <div className="employer-quick-stat-item">
                  <div className="employer-quick-stat-icon">
                    <FaUsers />
                  </div>
                  <div>
                    <h4>{stats.total_hired}</h4>
                    <p>{t('hiredWomen')}</p>
                  </div>
                </div>
                <div className="employer-quick-stat-item">
  <div className="employer-quick-stat-icon">
    <FaCheckCircle />
  </div>
  <div>
    <h4>{stats.approved_requests || 0}</h4>
    <p>{t('approved')}</p>
  </div>
</div>
                <div className="employer-quick-stat-item">
                  <div className="employer-quick-stat-icon">
                    <FaClock />
                  </div>
                  <div>
                    <h4>{stats.pending_requests}</h4>
                    <p>{t('pending')}</p>
                  </div>
                </div>
              </div>

              {/* Stats Overview */}
              <div className="employer-stats-overview">
                <div className="employer-stat-card primary">
                  <div className="employer-stat-icon">
                    <FaFileAlt />
                  </div>
                  <div className="employer-stat-content">
                    <h3>{stats.total_requests || 0}</h3>
                    <p>{t('totalRequests')}</p>
                    <div className="employer-stat-details">
                      <span className="employer-stat-trend positive">
                        {stats.approval_rate || 0}% {t('approvalRate')}
                      </span>
                      <small>{stats.pending_requests || 0} {t('pending')}</small>
                    </div>
                  </div>
                </div>

                <div className="employer-stat-card success">
                  <div className="employer-stat-icon">
                    <FaCheckCircle />
                  </div>
                  <div className="employer-stat-content">
                    <h3>{stats.approved_requests || 0}</h3>
                    <p>{t('approved')}</p>
                    <div className="employer-stat-details">
                      <span className="employer-stat-trend">
                        {stats.total_hired || 0} {t('womenHired')}
                      </span>
                      <small>{safeToFixed(stats.avg_employees_per_request)} {t('avgPerRequest')}</small>
                    </div>
                  </div>
                </div>

                <div className="employer-stat-card warning">
  <div className="employer-stat-icon">
    <FaUsers />
  </div>
  <div className="employer-stat-content">
    <h3>{stats.total_hired || 0}</h3>
    <p>{t('hiredWomen')}</p>
    <div className="employer-stat-details">
      <span className="employer-stat-trend positive">
        {t('average')}: {stats.avg_duration_days || 0} {t('days')}
      </span>
      <small>{safeToFixed(stats.avg_employees_per_request)} {t('perRequest')}</small>
    </div>
  </div>
</div>
                <div className="employer-stat-card info">
                  <div className="employer-stat-icon">
                    <FaUsers />
                  </div>
                  <div className="employer-stat-content">
                    <h3>{stats.total_hired || 0}</h3>
                    <p>{t('hiredWomen')}</p>
                    <div className="employer-stat-details">
                      <span className="employer-stat-trend positive">
                        {t('average')}: {stats.avg_duration_days || 0} {t('days')}
                      </span>
                      <small>{safeToFixed(stats.avg_employees_per_request)} {t('perRequest')}</small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="employer-content-section">
                <h2><FaBolt /> {t('quickActions')}</h2>
                <div className="employer-quick-actions">
                  <button 
                    className="employer-action-btn primary"
                    onClick={() => setShowRequestModal(true)}
                  >
                    <FaPlus />
                    <span>{t('newRequest')}</span>
                    <small>{t('requestTrainedWomen')}</small>
                  </button>
                 
                  <button 
                    className="employer-action-btn warning"
                    onClick={exportRequests}
                    disabled={exporting}
                  >
                    {exporting ? <FaSpinner className="employer-spinning" /> : <FaDownload />}
                    <span>{t('exportData')}</span>
                    <small>{t('downloadAllRequests')}</small>
                  </button>
                  <button 
                    className="employer-action-btn info"
                    onClick={() => setActiveMenu('hired')}
                  >
                    <FaBuilding />
                    <span>{t('browseHired')}</span>
                    <small>{t('viewHiredWomen')}</small>
                  </button>
                </div>
              </div>

              {/* Recent Activity & Notifications */}
              <div className="employer-dashboard-grid">
                <div className="employer-content-section">
                  <div className="employer-section-header">
                    <h2><FaHistory /> {t('recentActivity')}</h2>
                    <button className="employer-btn-link" onClick={() => setActiveMenu('requests')}>
                      {t('viewAll')} <FaChevronRight />
                    </button>
                  </div>
                  <div className="employer-activity-list">
                    {requests.slice(0, 5).map(request => (
                      <div key={request.id} className="employer-activity-item">
                        <div className="employer-activity-icon">
                          {request.status === 'pending' ? <FaClock /> : 
                           request.status === 'approved' ? <FaCheckCircle /> : 
                           <FaTimesCircle />}
                        </div>
                        <div className="employer-activity-details">
                          <h4>{request.department_name || t('unknownDepartment')}</h4>
                          <p>
  {request.number_of_employees || 0} {t('employees')} • 
  {request.duration_days || 0} {t('days')}
</p>
                          <span className={`employer-status-badge employer-status-${request.status || 'pending'}`}>
                            {t(request.status) || request.status || t('pending')}
                          </span>
                        </div>
                        <div className="employer-activity-time">
                          {request.created_at ? new Date(request.created_at).toLocaleDateString() : t('unknownDate')}
                        </div>
                      </div>
                    ))}
                    {requests.length === 0 && (
                      <div className="employer-no-activity">
                        <p>{t('noRecentActivity') || 'No recent activity'}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="employer-content-section">
                  <div className="employer-section-header">
                    <h2><FaBell /> {t('notifications')}</h2>
                    <button className="employer-btn-link" onClick={() => setNotifications([])}>
                      {t('clearAll')}
                    </button>
                  </div>
                  <div className="employer-notifications-list">
                    {notifications.length > 0 ? (
                      notifications.map(notification => (
                        <div 
                          key={notification.id} 
                          className={`employer-notification-item ${notification.type}`}
                          onClick={notification.action}
                        >
                          <div className="employer-notification-icon">
                            {notification.type === 'warning' && <FaClock />}
                            
                            {notification.type === 'success' && <FaCheckCircle />}
                          </div>
                          <div className="employer-notification-content">
                            <p>{notification.message}</p>
                            <span>{notification.time}</span>
                          </div>
                          <FaChevronRight />
                        </div>
                      ))
                    ) : (
                      <div className="employer-no-notifications">
                        <FaCheckCircle />
                        <p>{t('allCaughtUp') || 'All caught up!'}</p>
                        <small>{t('noNewNotifications') || 'No new notifications'}</small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Requests Management */}
          {activeMenu === 'requests' && (
            <div className="employer-content-section">
              <div className="employer-section-header">
                <h2>{t('myRequests')}</h2>
                <div className="employer-section-actions">
                  <div className="employer-search-filter">
                    <FaSearch />
                    <input
                      type="text"
                      placeholder={t('searchRequests') || 'Search requests...'}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <button 
                    className="employer-btn employer-btn-primary" 
                    onClick={() => setShowRequestModal(true)}
                  >
                    <FaPlus /> {t('newRequest')}
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div className="employer-filter-section">
                <div className="employer-filter-group">
                  <label><FaFilter /> {t('filterBy')}:</label>
                  <select 
                    className="employer-filter-select"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="all">{t('allStatus')}</option>
                    <option value="pending">{t('pending')}</option>
                    <option value="approved">{t('approved')}</option>
                    <option value="rejected">{t('rejected')}</option>
                    <option value="cancelled">{t('cancelled')}</option>
                  </select>
                  <select 
                    className="employer-filter-select"
                    value={filters.department}
                    onChange={(e) => handleFilterChange('department', e.target.value)}
                  >
                    <option value="all">{t('allDepartments')}</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                  <div className="employer-date-filter">
                    <FaCalendar />
                    <input
                      type="date"
                      value={filters.start}
                      onChange={(e) => handleFilterChange('start', e.target.value)}
                    />
                    <span>{t('to') || 'to'}</span>
                    <input
                      type="date"
                      value={filters.end}
                      onChange={(e) => handleFilterChange('end', e.target.value)}
                    />
                  </div>
                  <button 
                    className="employer-btn employer-btn-outline"
                    onClick={resetFilters}
                  >
                    {t('clearFilters')}
                  </button>
                </div>
              </div>

              {filteredRequests.length === 0 ? (
  <div className="employer-empty-state">
    <FaFileAlt size={48} />
    <h3>{t('noRequestsFound') || 'No Requests Found'}</h3>
    <p>{searchTerm || filters.status !== 'all' ? t('noRequestsMatch') || 'No requests match your criteria.' : t('No Requests Made') || "You haven't made any requests yet."}</p>
    <button className="employer-btn employer-btn-primary employer-btn-sm" onClick={() => setShowRequestModal(true)}>
      <FaPlus /> {t('Make First Request') || 'Make Your First Request'}
    </button>
  </div>
              ) : (
                <>
                  <div className="employer-table-summary">
                    <p>{t('showingRequests', { count: filteredRequests.length }) || `Showing ${filteredRequests.length} request${filteredRequests.length !== 1 ? 's' : ''}`}</p>
                    <button 
                      className="employer-btn employer-btn-outline" 
                      onClick={exportRequests}
                      disabled={exporting}
                    >
                      {exporting ? <FaSpinner className="employer-spinning" /> : <FaDownload />}
                      {t('exportAll') || 'Export All'}
                    </button>
                  </div>
                  <div className="employer-table-responsive">
                    <table className="employer-data-table employer-requests-table">
                    <thead>
  <tr>
    <th>{t('id') || 'ID'}</th>
    <th>{t('date')}</th>
    <th>{t('department')}</th>
    <th>{t('employees')}</th>
    <th>{t('workType')}</th>
    <th>{t('contractPeriod') || 'Contract Period'}</th> {/* Changed from Duration */}
    <th>{t('specifications')}</th>
    
    <th>{t('status')}</th>
    <th>{t('actions')}</th>
  </tr>
</thead>
                      <tbody>
                        {filteredRequests.map(request => (
                          <React.Fragment key={request.id}>
                            <tr 
                              className={expandedRow === request.id ? 'employer-expanded-row' : ''}
                              onClick={() => toggleRowExpansion(request.id)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td>REQ-{request.id}</td>
                              <td>{request.created_at ? new Date(request.created_at).toLocaleDateString() : 'N/A'}</td>
                              <td>
                                <div className="employer-department-cell">
                                  <FaBuilding />
                                  <span>{request.department_name || 'N/A'}</span>
                                </div>
                              </td>
                              <td>{request.number_of_employees || 0}</td>
                              <td>
                                <span className="employer-work-type-badge">
                                  {request.work_type ? t(request.work_type) || request.work_type : '-'}
                                </span>
                              </td>
                              <td>
  <span className="employer-contract-period-badge">
    {formatContractPeriod(request)}
  </span>
</td>
                              <td>
                                <div className="employer-specs-preview">
                                  {request.age_range ? (
                                    <span className="employer-spec-tag">
                                      <FaUser /> {formatSpecification(request.age_range, 'age')}
                                    </span>
                                  ) : null}
                                  {request.experience_level ? (
                                    <span className="employer-spec-tag">
                                      <FaAward /> {formatSpecification(request.experience_level, 'experience')}
                                    </span>
                                  ) : null}
                                  {request.skill_level ? (
                                    <span className="employer-spec-tag">
                                      <FaTools /> {formatSpecification(request.skill_level, 'skill')}
                                    </span>
                                  ) : null}
                                  <button 
                                    className="employer-specs-toggle"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleRowExpansion(request.id);
                                    }}
                                  >
                                    <FaInfo />
                                  </button>
                                </div>
                              </td>
                              
                              <td>
                              <span className={`employer-status-badge employer-status-${request.status || 'pending'}`}>
  {t(request.status) || request.status || t('pending')}
</span>
                              </td>
                              <td onClick={(e) => e.stopPropagation()}>
                                <div className="employer-action-buttons">
                                  <button 
                                    className="employer-btn-icon employer-btn-icon view"
                                    onClick={() => toggleRowExpansion(request.id)}
                                    title={t('viewDetails')}
                                  >
                                    {expandedRow === request.id ? <FaChevronUp /> : <FaEye />}
                                  </button>
                                  <button 
                                    className="employer-btn-icon employer-btn-icon edit"
                                    onClick={() => handleEditRequest(request)}
                                    title={t('editRequest')}
                                  >
                                    <FaEdit />
                                  </button>
                                 
                                  {request.status === 'pending' && (
                                    <>
                                      <button 
                                        className="employer-btn-icon employer-btn-icon delete"
                                        onClick={() => handleDeleteRequest(request)}
                                        title={t('deleteRequest')}
                                      >
                                        <FaTrash />
                                      </button>
                                      <button 
                                        className="employer-btn-icon employer-btn-icon cancel"
                                        onClick={() => handleCancelRequest(request)}
                                        title={t('cancelRequest')}
                                      >
                                        <FaTimesCircle />
                                      </button>
                                    </>
                                  )}
                                  
                                </div>
                              </td>
                            </tr>
                         
{expandedRow === request.id && (
  <tr className="employer-details-row">
    <td colSpan="10">
      <div className="employer-request-details">
        <h4>Request Details - REQ-{request.id}</h4>
        <div className="employer-details-grid">
          <div className="employer-details-section">
            <h5><FaUser /> Contact Information</h5>
            <p><strong>Employer:</strong> {request.employer_name || '-'}</p>
            <p><strong>Email:</strong> {request.employer_email || '-'}</p>
            <p><strong>Phone:</strong> {request.employer_phone || '-'}</p>
            <p><strong>Company:</strong> {user?.company_name || '-'}</p>
          </div>
          
          <div className="employer-details-section">
            <h5><FaCalendar /> Job Details</h5>
            <div className="employer-specs-grid">
              <div className="employer-spec-item">
                <FaBuilding />
                <div>
                  <small>Department</small>
                  <p>{request.department_name || '-'}</p>
                </div>
              </div>
              <div className="employer-spec-item">
                <FaUsers />
                <div>
                  <small>Employees Needed</small>
                  <p>{request.number_of_employees || 0}</p>
                </div>
              </div>
              <div className="employer-spec-item">
                <FaBriefcase />
                <div>
                  <small>Work Type</small>
                  <p>{request.work_type ? t(request.work_type) || request.work_type : '-'}</p>
                </div>
              </div>
              {/* Add Contract Period */}
              <div className="employer-spec-item">
                <FaCalendarAlt />
                <div>
                  <small>Contract Period</small>
                  <p>{request.contract_period_name || request.duration_days + ' days' || '-'}</p>
                </div>
              </div>
              
            </div>
          </div>
          
          <div className="employer-details-section">
            <h5><FaTools /> Specifications</h5>
            <div className="employer-specs-grid">
              <div className="employer-spec-item">
                <FaUser />
                <div>
                  <small>Age Range</small>
                  <p>{formatSpecification(request.age_range, 'age')}</p>
                </div>
              </div>
              <div className="employer-spec-item">
                <FaAward />
                <div>
                  <small>Experience</small>
                  <p>{formatSpecification(request.experience_level, 'experience')}</p>
                </div>
              </div>
              <div className="employer-spec-item">
                <FaTools />
                <div>
                  <small>Skill Level</small>
                  <p>{formatSpecification(request.skill_level, 'skill')}</p>
                </div>
              </div>
              <div className="employer-spec-item">
                <FaSchool />
                <div>
                  <small>Education</small>
                  <p>{formatSpecification(request.education_level, 'education')}</p>
                </div>
              </div>
              <div className="employer-spec-item">
                <FaLanguage />
                <div>
                  <small>Languages</small>
                  <p>{formatSpecification(request.language_requirements, 'language')}</p>
                </div>
              </div>
            </div>
            
            {/* Add Competence Skills Section */}
            {request.competence_skills && request.competence_skills.length > 0 && (
              <div className="employer-competence-skills">
                <h6><FaCertificate /> Competence Skills</h6>
                <div className="employer-skills-list">
                  {Array.isArray(request.competence_skills) ? (
                    request.competence_skills.map((skill, index) => (
                      <span key={index} className="employer-skill-tag">
                        {skill.skill_name || skill}
                      </span>
                    ))
                  ) : (
                    <span className="employer-skill-tag">
                      {request.competence_skills}
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {request.special_skills && request.special_skills !== '0' && (
              <div className="employer-special-skills">
                <h6><FaStar /> Special Skills</h6>
                <p>{request.special_skills}</p>
              </div>
            )}
            
            {request.other_specifications && (
              <div className="employer-other-specs">
                <h6><FaInfoCircle /> Other Requirements</h6>
                <p>{request.other_specifications}</p>
              </div>
            )}
          </div>
          
          <div className="employer-details-section">
            <h5><FaInfoCircle /> Request Information</h5>
            <p><strong>Status:</strong> 
              <span className={`employer-status-badge employer-status-${request.status || 'pending'}`}>
                {t(request.status) || request.status || t('pending')}
                
              </span>
            </p>
            <p><strong>Requested Woman:</strong> {request.requested_woman_name || 'None (General Request)'}</p>
            <p><strong>Created:</strong> {request.created_at ? new Date(request.created_at).toLocaleString() : '-'}</p>
            <p><strong>Last Updated:</strong> {request.updated_at ? new Date(request.updated_at).toLocaleString() : '-'}</p>
            
            <p><strong>Assigned Women:</strong> {request.assigned_women_count || 0} / {request.number_of_employees || 0}</p>
          </div>
        </div>
      </div>
    </td>
  </tr>
)}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Hired Women */}
          {activeMenu === 'hired' && (
            <div className="employer-content-section">
              <div className="employer-section-header">
                <h2>{t('hiredWomen')}</h2>
                <div className="employer-section-actions">
                  <button className="employer-btn employer-btn-outline" onClick={exportRequests} disabled={exporting}>
                    {exporting ? <FaSpinner className="employer-spinning" /> : <FaDownload />} {t('exportList') || 'Export List'}
                  </button>
                </div>
              </div>

              {hiredWomen.length > 0 ? (
                <div className="employer-employees-grid">
                  {hiredWomen.map((woman, index) => (
                    <div key={woman.id || index} className="employer-employee-card">
                      <div className="employer-employee-photo">
                        <img 
                          src={woman.photo_url || '/default-avatar.png'} 
                          alt={woman.name}
                          onError={(e) => {
                            e.target.src = '/default-avatar.png';
                          }}
                        />
                        <span className={`employer-employee-status ${woman.is_available ? 'available' : 'hired'}`}>
                          {woman.is_available ? t('available') : t('hired')}
                        </span>
                      </div>
                      <div className="employer-employee-info">
                        <h3>{woman.name || t('unknownName')}</h3>
                        <p className="employer-employee-department">
                          <FaBuilding /> {woman.department_name || t('unknownDepartment')}
                        </p>
                        <div className="employer-employee-details">
                          <span><FaCalendar /> {woman.experience_years || 0} {t('yearsExp')}</span>
                          <span><FaUser /> {t('hiredOn')} {woman.hired_date ? new Date(woman.hired_date).toLocaleDateString() : t('unknown')}</span>
                          {woman.phone && <span><FaPhone /> {woman.phone}</span>}
                          {woman.email && <span><FaEnvelope /> {woman.email}</span>}
                        </div>
                        <div className="employer-employee-skills">
                          <FaGraduationCap /> {t('skills')}: {woman.skills || t('notSpecified')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="employer-empty-state">
                  <FaUsers size={48} />
                  <h3>{t('noHiredWomen') || 'No Hired Women'}</h3>
                  <p>{t('noHiredWomenDesc') || "You haven't hired any women yet. Approved requests will appear here."}</p>
                  <div className="employer-empty-state-actions">
  <button className="employer-btn employer-btn-primary employer-btn-sm" onClick={() => setShowRequestModal(true)}>
    <FaPlus /> {t('makeRequest') || 'Make a Request'}
  </button>
  <button className="employer-btn employer-btn-outline employer-btn-sm" onClick={fetchHiredWomen}>
    <FaSync /> {t('refresh')}
  </button>
</div>
                </div>
              )}
            </div>
          )}

         
{/* Contracts Management Section */}
{activeMenu === 'contracts' && (
  <div className="employer-content-section">
    <div className="employer-section-header">
      <h2><FaFileAlt /> {t('contracts') || 'My Contracts'}</h2>
      <div className="employer-section-actions">
        <div className="employer-search-filter">
          <FaSearch />
          <input
            type="text"
            placeholder={t('searchContracts') || 'Search contracts...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button 
          className="employer-btn employer-btn-outline" 
          onClick={fetchContracts}
          disabled={refreshing}
        >
          {refreshing ? <FaSpinner className="employer-spinning" /> : <FaSync />}
          {t('refresh')}
        </button>
      </div>
    </div>

    {/* Contract Stats Summary */}
    <div className="employer-payments-summary">
      <div className="employer-summary-card">
        <div className="employer-summary-icon">
          <FaCheckCircle />
        </div>
        <div className="employer-summary-content">
          <h3>{contractStats.active || 0}</h3>
          <p>{t('activeContracts') || 'Active Contracts'}</p>
        </div>
      </div>
      <div className="employer-summary-card">
        <div className="employer-summary-icon">
          <FaClock />
        </div>
        <div className="employer-summary-content">
          <h3>{contractStats.expiringSoon || 0}</h3>
          <p>{t('expiringSoon') || 'Expiring Soon'}</p>
        </div>
      </div>
      <div className="employer-summary-card">
        <div className="employer-summary-icon">
          <FaHistory />
        </div>
        <div className="employer-summary-content">
          <h3>{contractStats.completed || 0}</h3>
          <p>{t('completedContracts') || 'Completed'}</p>
        </div>
      </div>
    </div>

    {/* Tabs */}
    <div className="employer-tabs">
      <button 
        className={`employer-tab ${contractTab === 'active' ? 'active' : ''}`}
        onClick={() => setContractTab('active')}
      >
        <FaCheckCircle /> {t('activeContracts') || 'Active Contracts'}
      </button>
      <button 
        className={`employer-tab ${contractTab === 'history' ? 'active' : ''}`}
        onClick={() => setContractTab('history')}
      >
        <FaHistory /> {t('contractHistory') || 'Contract History'}
      </button>
    </div>

    {contractTab === 'active' && (
      <>
        {activeContracts.length === 0 ? (
          <div className="employer-empty-state">
            <FaFileAlt size={48} />
            <h3>{t('noActiveContracts') || 'No Active Contracts'}</h3>
            <p>{t('noActiveContractsDesc') || "You don't have any active contracts at the moment."}</p>
          </div>
        ) : (
          <div className="employer-contracts-grid">
            {activeContracts.map(contract => (
              <div key={contract.id} className={`employer-contract-card ${contract.days_remaining <= 7 ? 'expiring-soon' : ''}`}>
                <div className="employer-contract-header">
                  <div className="employer-contract-woman">
                    <div className="employer-contract-avatar">
                      {contract.photo_url ? (
                        <img src={contract.photo_url} alt={contract.woman_name} />
                      ) : (
                        <div className="employer-avatar-small">
                          {contract.woman_name?.charAt(0) || 'W'}
                        </div>
                      )}
                    </div>
                    <div>
                      <h4>{contract.woman_name}</h4>
                      <p className="employer-contract-department">
                        <FaBuilding /> {contract.department_name}
                      </p>
                    </div>
                  </div>
                  <span className={`employer-contract-status-badge ${contract.contract_status}`}>
                    {contract.days_remaining <= 0 ? t('expired') : 
                     contract.days_remaining <= 7 ? t('expiringSoon') : 
                     t('active')}
                  </span>
                </div>

                <div className="employer-contract-details">
                  <div className="employer-contract-info-row">
                    <FaCalendarAlt />
                    <span>
                      <strong>{t('period')}:</strong> {contract.period_name || `${contract.months || 3} months`}
                    </span>
                  </div>
                  <div className="employer-contract-info-row">
                    <FaClock />
                    <span>
                      <strong>{t('startDate')}:</strong> {new Date(contract.start_date).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="employer-contract-info-row">
                    <FaClock />
                    <span>
                      <strong>{t('endDate')}:</strong> {new Date(contract.end_date).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="employer-contract-info-row">
                    <FaUsers />
                    <span>
                      <strong>{t('skills')}:</strong> {contract.skills || t('notSpecified')}
                    </span>
                  </div>
                  {contract.days_remaining > 0 && (
                    <div className="employer-contract-info-row employer-days-remaining">
                      <FaExclamationTriangle />
                      <span className={contract.days_remaining <= 7 ? 'text-danger' : 'text-warning'}>
                        <strong>{t('daysRemaining')}:</strong> {contract.days_remaining} {t('days')}
                      </span>
                    </div>
                  )}
                </div>

                <div className="employer-contract-footer">
                  <button 
                    className="employer-btn employer-btn-outline"
                    onClick={() => viewContractDetails(contract.id)}
                  >
                    <FaEye /> {t('viewDetails')}
                  </button>
                  
                  {contract.days_remaining > 0 ? (
                    <button 
                      className="employer-btn employer-btn-primary"
                      onClick={() => openRenewContractModal(contract)}
                      disabled={contract.days_remaining > 60} // Can't renew too early
                    >
                      <FaSync /> {t('renew')}
                    </button>
                  ) : (
                    <button 
                      className="employer-btn employer-btn-success"
                      onClick={() => openRenewContractModal(contract)}
                    >
                      <FaSync /> {t('renewNow')}
                    </button>
                  )}
                  
                  <button 
                    className="employer-btn employer-btn-danger"
                    onClick={() => openTerminateContractModal(contract)}
                  >
                    <FaTimesCircle /> {t('terminate')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </>
    )}

    {contractTab === 'history' && (
      <>
        {contractHistory.length === 0 ? (
          <div className="employer-empty-state">
            <FaHistory size={48} />
            <h3>{t('noContractHistory') || 'No Contract History'}</h3>
            <p>{t('noContractHistoryDesc') || "You don't have any completed or terminated contracts."}</p>
          </div>
        ) : (
          <div className="employer-table-responsive">
            <table className="employer-data-table">
              <thead>
                <tr>
                  <th>{t('woman')}</th>
                  <th>{t('department')}</th>
                  <th>{t('period')}</th>
                  <th>{t('startDate')}</th>
                  <th>{t('endDate')}</th>
                  <th>{t('status')}</th>
                  <th>{t('terminationReason')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {contractHistory.map(contract => (
                  <tr key={contract.id}>
                    <td>
                      <div className="employer-user-cell">
                        <div className="employer-user-avatar-small">
                          {contract.woman_name?.charAt(0) || 'W'}
                        </div>
                        {contract.woman_name}
                      </div>
                    </td>
                    <td>{contract.department_name}</td>
                    <td>{contract.period_name || `${contract.months} months`}</td>
                    <td>{new Date(contract.start_date).toLocaleDateString()}</td>
                    <td>{new Date(contract.end_date).toLocaleDateString()}</td>
                    <td>
                      <span className={`employer-status-badge employer-status-${contract.status}`}>
                        {t(contract.status)}
                      </span>
                    </td>
                    <td>{contract.termination_reason || '-'}</td>
                    <td>
                      <button 
                        className="employer-btn-icon employer-btn-icon view"
                        onClick={() => viewContractDetails(contract.id)}
                        title={t('viewDetails')}
                      >
                        <FaEye />
                      </button>
                      {contract.status === 'expired' && (
                        <button 
                          className="employer-btn-icon employer-btn-icon renew"
                          onClick={() => openRenewContractModal({
                            id: contract.id,
                            woman_name: contract.woman_name,
                            woman_id: contract.woman_id,
                            department_name: contract.department_name,
                            period_name: contract.period_name,
                            months: contract.months
                          })}
                          title={t('renew')}
                        >
                          <FaSync />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    )}

    {/* Renew Contract Modal */}
    {showRenewModal && selectedContract && (
      <div className="employer-modal-overlay">
        <div className="employer-modal">
          <div className="employer-modal-header">
            <h3>{t('renewContract')} - {selectedContract.woman_name}</h3>
            <button className="employer-modal-close" onClick={() => setShowRenewModal(false)}>
              <FaTimes />
            </button>
          </div>
          <div className="employer-modal-body">
            <div className="employer-renew-info">
              <p>{t('renewContractInfo') || 'Current contract expires on:'}</p>
              <p className="employer-renew-date">
                <strong>{selectedContract.end_date ? new Date(selectedContract.end_date).toLocaleDateString() : ''}</strong>
              </p>
            </div>
            
            <div className="employer-form-group">
              <label>{t('contractPeriod')} *</label>
              <select 
                value={renewData.contract_period_id}
                onChange={(e) => setRenewData(prev => ({ ...prev, contract_period_id: e.target.value }))}
                required
              >
                <option value="">{t('selectContractPeriod')}</option>
                {contractPeriods.map(period => (
                  <option key={period.id} value={period.id}>
                    {period.period_name} ({period.months} months)
                  </option>
                ))}
                <option value="custom">{t('customMonths')}...</option>
              </select>
            </div>
            
            {renewData.contract_period_id === 'custom' && (
              <div className="employer-form-group">
                <label>{t('months')}</label>
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={renewData.months}
                  onChange={(e) => setRenewData(prev => ({ ...prev, months: e.target.value }))}
                  placeholder="Enter number of months"
                />
              </div>
            )}
            
            <div className="employer-form-group">
              <label>
                <input
                  type="checkbox"
                  checked={renewData.send_email_notification}
                  onChange={(e) => setRenewData(prev => ({ ...prev, send_email_notification: e.target.checked }))}
                />
                <span>{t('sendEmailNotification') || 'Send email confirmation'}</span>
              </label>
            </div>
            
            <div className="employer-alert employer-alert-info">
              <FaInfoCircle />
              <p>
                {t('renewContractNote') || 
                  'Renewing the contract will create a new contract period. The current contract will be marked as renewed.'}
              </p>
            </div>
          </div>
          <div className="employer-modal-footer">
            <button 
              className="employer-btn employer-btn-outline" 
              onClick={() => setShowRenewModal(false)}
            >
              {t('cancel')}
            </button>
            <button 
              className="employer-btn employer-btn-primary"
              onClick={handleRenewContract}
              disabled={!renewData.contract_period_id || renewLoading}
            >
              {renewLoading ? <FaSpinner className="employer-spinning" /> : <FaSync />}
              {renewLoading ? t('renewing') : t('renewContract')}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Terminate Contract Modal */}
    {showTerminateModal && selectedContract && (
      <div className="employer-modal-overlay">
        <div className="employer-modal">
          <div className="employer-modal-header">
            <h3>{t('terminateContract')} - {selectedContract.woman_name}</h3>
            <button className="employer-modal-close" onClick={() => setShowTerminateModal(false)}>
              <FaTimes />
            </button>
          </div>
          <div className="employer-modal-body">
            <div className="employer-alert employer-alert-warning">
              <FaExclamationTriangle />
              <p><strong>{t('warning')}:</strong> {t('terminateContractWarning') || 'This action cannot be undone.'}</p>
            </div>
            
            <div className="employer-form-group">
              <label>{t('terminationReason')} *</label>
              <select
                value={terminateData.reason}
                onChange={(e) => setTerminateData(prev => ({ ...prev, reason: e.target.value }))}
                required
              >
                <option value="">{t('selectReason')}</option>
                <option value="work_completed">{t('workCompleted') || 'Work completed'}</option>
                <option value="performance">{t('performance') || 'Performance issues'}</option>
                <option value="budget">{t('budgetConstraints') || 'Budget constraints'}</option>
                <option value="restructuring">{t('restructuring') || 'Company restructuring'}</option>
                <option value="mutual">{t('mutualAgreement') || 'Mutual agreement'}</option>
                <option value="other">{t('other')}</option>
              </select>
            </div>
            
            {terminateData.reason === 'other' && (
              <div className="employer-form-group">
                <label>{t('customReason')}</label>
                <textarea
                  value={terminateData.custom_reason}
                  onChange={(e) => setTerminateData(prev => ({ ...prev, custom_reason: e.target.value }))}
                  rows="3"
                  placeholder={t('enterTerminationReason')}
                />
              </div>
            )}
            
            <div className="employer-form-group">
              <label>{t('effectiveDate')}</label>
              <input
                type="date"
                value={terminateData.effective_date}
                onChange={(e) => setTerminateData(prev => ({ ...prev, effective_date: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
              />
              <small>{t('effectiveDateNote') || 'Leave empty for immediate termination'}</small>
            </div>
            
            <div className="employer-form-group">
              <label>
                <input
                  type="checkbox"
                  checked={terminateData.send_email_notification}
                  onChange={(e) => setTerminateData(prev => ({ ...prev, send_email_notification: e.target.checked }))}
                />
                <span>{t('sendTerminationEmail') || 'Send termination confirmation email'}</span>
              </label>
            </div>
          </div>
          <div className="employer-modal-footer">
            <button 
              className="employer-btn employer-btn-outline" 
              onClick={() => setShowTerminateModal(false)}
            >
              {t('cancel')}
            </button>
            <button 
              className="employer-btn employer-btn-danger"
              onClick={handleTerminateContract}
              disabled={!terminateData.reason || terminateLoading}
            >
              {terminateLoading ? <FaSpinner className="employer-spinning" /> : <FaTimesCircle />}
              {terminateLoading ? t('terminating') : t('terminateContract')}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Contract Details Modal */}
    {showContractDetailsModal && selectedContract && (
      <div className="employer-modal-overlay">
        <div className="employer-modal employer-modal-lg">
          <div className="employer-modal-header">
            <h3>{t('contractDetails')} - {selectedContract.woman_name}</h3>
            <button className="employer-modal-close" onClick={() => setShowContractDetailsModal(false)}>
              <FaTimes />
            </button>
          </div>
          <div className="employer-modal-body">
            <div className="employer-details-grid">
              <div className="employer-details-section">
                <h4><FaUser /> {t('womanInformation')}</h4>
                <div className="employer-detail-item">
                  <label>{t('name')}:</label>
                  <span>{selectedContract.woman_name}</span>
                </div>
                <div className="employer-detail-item">
                  <label>{t('age')}:</label>
                  <span>{selectedContract.age || t('n/a')}</span>
                </div>
                <div className="employer-detail-item">
                  <label>{t('department')}:</label>
                  <span>{selectedContract.department_name}</span>
                </div>
                <div className="employer-detail-item">
                  <label>{t('skills')}:</label>
                  <span>{selectedContract.skills || t('n/a')}</span>
                </div>
                <div className="employer-detail-item">
                  <label>{t('experience')}:</label>
                  <span>{selectedContract.experience_years || 0} {t('years')}</span>
                </div>
                {selectedContract.woman_phone && (
                  <div className="employer-detail-item">
                    <label>{t('phone')}:</label>
                    <span>{selectedContract.woman_phone}</span>
                  </div>
                )}
                {selectedContract.woman_email && (
                  <div className="employer-detail-item">
                    <label>{t('email')}:</label>
                    <span>{selectedContract.woman_email}</span>
                  </div>
                )}
              </div>
              
              <div className="employer-details-section">
                <h4><FaFileAlt /> {t('contractInformation')}</h4>
                <div className="employer-detail-item">
                  <label>{t('contractId')}:</label>
                  <span>CTR-{selectedContract.id}</span>
                </div>
                <div className="employer-detail-item">
                  <label>{t('period')}:</label>
                  <span>{selectedContract.period_name || `${selectedContract.months} months`}</span>
                </div>
                <div className="employer-detail-item">
                  <label>{t('startDate')}:</label>
                  <span>{new Date(selectedContract.start_date).toLocaleDateString()}</span>
                </div>
                <div className="employer-detail-item">
                  <label>{t('endDate')}:</label>
                  <span>{new Date(selectedContract.end_date).toLocaleDateString()}</span>
                </div>
                <div className="employer-detail-item">
                  <label>{t('daysRemaining')}:</label>
                  <span className={selectedContract.days_remaining <= 7 ? 'text-danger' : ''}>
                    {selectedContract.days_remaining} {t('days')}
                  </span>
                </div>
                <div className="employer-detail-item">
                  <label>{t('status')}:</label>
                  <span className={`employer-status-badge employer-status-${selectedContract.contract_status || selectedContract.status}`}>
                    {selectedContract.contract_status || selectedContract.status}
                  </span>
                </div>
                <div className="employer-detail-item">
                  <label>{t('requestId')}:</label>
                  <span>REQ-{selectedContract.request_id}</span>
                </div>
              </div>
              
              {selectedContract.termination_reason && (
                <div className="employer-details-section">
                  <h4><FaInfoCircle /> {t('terminationDetails')}</h4>
                  <div className="employer-detail-item">
                    <label>{t('reason')}:</label>
                    <span>{selectedContract.termination_reason}</span>
                  </div>
                  {selectedContract.terminated_at && (
                    <div className="employer-detail-item">
                      <label>{t('terminatedOn')}:</label>
                      <span>{new Date(selectedContract.terminated_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  {selectedContract.effective_date && (
                    <div className="employer-detail-item">
                      <label>{t('effectiveDate')}:</label>
                      <span>{new Date(selectedContract.effective_date).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              )}
              
              {selectedContract.renewed_from && (
                <div className="employer-details-section">
                  <h4><FaSync /> {t('renewalInformation')}</h4>
                  <div className="employer-detail-item">
                    <label>{t('renewedFrom')}:</label>
                    <span>CTR-{selectedContract.renewed_from}</span>
                  </div>
                  {selectedContract.renewed_to && (
                    <div className="employer-detail-item">
                      <label>{t('renewedTo')}:</label>
                      <span>CTR-{selectedContract.renewed_to}</span>
                    </div>
                  )}
                </div>
              )}
              
              {selectedContract.notes && (
                <div className="employer-details-section full-width">
                  <h4>{t('notes')}</h4>
                  <p>{selectedContract.notes}</p>
                </div>
              )}
            </div>
          </div>
          <div className="employer-modal-footer">
            <button 
              className="employer-btn employer-btn-outline" 
              onClick={() => setShowContractDetailsModal(false)}
            >
              {t('close')}
            </button>
            {selectedContract.status === 'active' && (
              <>
                <button 
                  className="employer-btn employer-btn-primary"
                  onClick={() => {
                    setShowContractDetailsModal(false);
                    openRenewContractModal(selectedContract);
                  }}
                >
                  <FaSync /> {t('renew')}
                </button>
                <button 
                  className="employer-btn employer-btn-danger"
                  onClick={() => {
                    setShowContractDetailsModal(false);
                    openTerminateContractModal(selectedContract);
                  }}
                >
                  <FaTimesCircle /> {t('terminate')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )}
  </div>
)}
   {/* Settings */}
{activeMenu === 'settings' && (
  <div className="employer-content-section">
    <h2>{t('settings')}</h2>
    <div className="employer-settings-grid">
      <div className="employer-setting-card">
        <h3><FaUser /> {t('profileSettings') || 'Profile Settings'}</h3>
        <div className="employer-setting-options">
          <div className="employer-setting-item">
            <label>{t('fullName') || 'Full Name'} *</label>
            <input
              type="text"
              value={profileData.full_name}
              onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))}
              placeholder={t('enterFullName') || 'Enter your full name'}
            />
          </div>

          <div className="employer-setting-item">
            <label>{t('emailAddress') || 'Email Address'} *</label>
            <input
              type="email"
              value={profileData.email}
              onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
              placeholder={t('enterEmail') || 'Enter your email'}
            />
          </div>

          <div className="employer-setting-item">
            <label>{t('phoneNumber') || 'Phone Number'}</label>
            <input
              type="tel"
              value={profileData.phone}
              onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder={t('enterPhone') || 'Enter your phone number'}
            />
          </div>

          <div className="employer-setting-item">
            <label>{t('companyName') || 'Company Name'}</label>
            <input
              type="text"
              value={profileData.company_name}
              onChange={(e) => setProfileData(prev => ({ ...prev, company_name: e.target.value }))}
              placeholder={t('enterCompanyName') || 'Enter your company name'}
            />
          </div>

          <button
            className="employer-btn employer-btn-primary"
            onClick={handleSaveProfile}
            disabled={savingProfile}
          >
            {savingProfile ? <FaSpinner className="employer-spinning" /> : <FaCheckCircle />}
            {savingProfile ? (t('saving') || 'Saving...') : (t('saveProfile') || 'Save Profile')}
          </button>
        </div>
      </div>

      <div className="employer-setting-card">
        <h3><FaCog /> {t('accountSettings') || 'Account Settings'}</h3>
        <div className="employer-setting-options">
          <div className="employer-setting-item">
            <label>{t('emailNotifications') || 'Email Notifications'}</label>
            <div className="employer-toggle-switch">
              <input type="checkbox" id="email-notifications" defaultChecked />
              <label htmlFor="email-notifications"></label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
)}
        </div>
      </main>

              

{showRequestModal && (
  <RequestModal
    isOpen={showRequestModal}
    onClose={() => setShowRequestModal(false)}
    onSubmit={handleRequestSubmit}
    departments={departments}
    employerInfo={user}
    contractPeriods={contractPeriods} // Add this prop
  />
)}

{showEditModal && selectedRequest && (
  <EditRequestModal
    isOpen={showEditModal}
    onClose={() => {
      setShowEditModal(false);
      setSelectedRequest(null);
    }}
    request={selectedRequest}
    departments={departments}
    onSubmit={handleUpdateRequest}
    contractPeriods={contractPeriods} // Add this prop
    competenceSkills={competenceSkills[selectedRequest.department_id]} // Add this prop
  />
)}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedRequest && (
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedRequest(null);
          }}
          onConfirm={confirmDeleteRequest}
          title={t('deleteRequest')}
          message={t('confirmDeleteRequest') || "Are you sure you want to delete this request? This action cannot be undone."}
          confirmText={t('delete')}
          cancelText={t('cancel')}
          type="danger"
        />
      )}
    </div>
  );
};

export default EmployerDashboard;